/******* Angular Resourse *******/
import { Component, ElementRef, OnInit, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

/******* Core Resourse *******/
import { CoreConfigService } from '@core/services/config.service';
import { passwordValidator, passwordMatch, confirmPasswordValidator } from '@core/validators';
import { UtilsService } from '@core/services/utils.service';
import { AccessTokenService } from '@core/services/access-token.service';
import { AccountSwitchService } from '@core/services/account-switch.service';

/******* Plug-In Resourse *******/
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

/******* API Resourse *******/
import { Auth, Users } from '@api';

/******* Modal Template *******/
import { TermsOfUseModalComponent } from './modal-template/terms-of-use.component';

/******* Environments Resourse *******/
import { environment } from 'environments/environment';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'app-reset-password',
	templateUrl: './reset-password.component.html',
	styleUrls: ['./reset-password.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class ResetPasswordComponent implements OnInit, OnDestroy {

	// Public
	public coreConfig: any;
	public resetPasswordForm: UntypedFormGroup;
	public passwordTextType: boolean = false;
	public confPasswordTextType: boolean = false;
	public submitted: boolean = false;
	public loading: boolean = false;

	// Private
	@ViewChild('modal_term_of_use') modelTemplate;
	private _unsubscribeAll$: Subject<any> = new Subject();
	private _queryParameter: any;
	private _isTwoFactorAuthenticationActivate: boolean = false;

	/**
	 * Constructor
	 */
	constructor(
		private _route: ActivatedRoute,
		private _router: Router,
		private _formBuilder: UntypedFormBuilder,
		private _elementRef: ElementRef,
		private _users: Users,
		private _auth: Auth,
		private _coreConfigService: CoreConfigService,
		private _ngbModal: NgbModal,
		private _utilsService: UtilsService,
		private _accessTokenService: AccessTokenService,
		private _accountSwitchService: AccountSwitchService
	,
                private _translate: TranslateService) {
		this.buildResetPasswordForm();
		this.configureLayout();
	}

	/**
	 * Accessors
	 */
	get f() {
		return this.resetPasswordForm.controls;
	}

	get formErrors() {
		return this.resetPasswordForm.errors;
	}

	get minlengthValid(): boolean {
		return this.f.newPassword.errors
			? !this.f.newPassword.errors.minlength && !this.f.newPassword.errors.required
			: true;
	}

	get digitValid(): boolean {
		return this.f.newPassword.errors ? !this.f.newPassword.errors.digit : true;
	}

	get capitalValid(): boolean {
		return this.f.newPassword.errors ? !this.f.newPassword.errors.capital : true;
	}

	get specialValid(): boolean {
		return this.f.newPassword.errors ? !this.f.newPassword.errors.special : true;
	}

	/**
	 * Lifecycle Hooks
	 */
	ngOnInit(): void {
		this._route.queryParams
			.pipe(takeUntil(this._unsubscribeAll$))
			.subscribe({
				next: (params) => this.handleResetPasswordVerification(params)
			});

		this._coreConfigService.config
			.pipe(takeUntil(this._unsubscribeAll$))
			.subscribe({
				next: (config) => this.coreConfig = config
			});
	}

	ngOnDestroy(): void {
		this._unsubscribeAll$.next(null);
		this._unsubscribeAll$.complete();
	}

	/**
	 * Public Methods
	 */

	/**
	 * Display Terms of Use Modal
	 */
	public displayTermsOfUse(event: Event): void {
		event.preventDefault();

		this._ngbModal.open(TermsOfUseModalComponent, {
			centered: true,
			size: 'lg'
		}).result.then(
			(result) => {
				if (result === "Accept click") {
					this.resetPasswordForm.get('termOfUse').setValue(true);
				}
			},
			() => { } // Dismiss handler
		);
	}

	/**
	 * Submit Reset Password Form
	 */
	public onSubmit(): void {
		this.submitted = true;

		if (this.resetPasswordForm.invalid) {
			this._utilsService.scrollToInvalid(this._elementRef);
			return;
		}

		this.loading = true;

		const inputData = {
			email: this._queryParameter.email,
			token: this._queryParameter.token,
			password: this.f.newPassword.value,
			isTwoFactorActivity: this._isTwoFactorAuthenticationActivate
		};

		this._users.resetPassword(inputData).subscribe({
			next: () => this.handleResetPasswordSuccess(),
			error: (error) => this.handleResetPasswordError(error)
		});
	}

	/**
	 * Private Methods
	 */

	/**
	 * Build Reset Password Form
	 */
	private buildResetPasswordForm(): void {
		this.resetPasswordForm = this._formBuilder.group({
			newPassword: [
				'',
				[
					Validators.required,
					Validators.minLength(8),
					Validators.maxLength(30),
					passwordValidator("digit"),
					passwordValidator("capital"),
					passwordValidator("special")
				]
			],
			confirmPassword: ['', [Validators.required, confirmPasswordValidator('newPassword')]],
			termOfUse: [false, [Validators.requiredTrue]]
		});
	}

	/**
	 * Configure Layout
	 */
	private configureLayout(): void {
		this._coreConfigService.setConfig({
			layout: {
				footer: { hidden: true },
				menu: { hidden: true },
				navbar: { hidden: true },
				buyNow: false,
				customizer: false,
				scrollTop: false,
				enableLocalStorage: false,
				skin: "dark",
				type: "vertical"
			}
		});
	}

	/**
	 * Handle Reset Password Verification
	 */
	private handleResetPasswordVerification(params: any): void {
		this._queryParameter = params;
		this._isTwoFactorAuthenticationActivate = params.isTwoFactorActivity === "true";
		this.verifyEmail(params);
	}

	/**
	 * Verify Email Reset Token
	 */
	private verifyEmail(params: any): void {
		const inputData = {
			email: params.email,
			token: params.token,
			isTwoFactorActivity: this._isTwoFactorAuthenticationActivate
		};

		this._users.verifyResetPassword(inputData).subscribe({
			next: (results) => {
				if (!results.isSuccessful) {
					this.handleVerificationError("Email verification failed. Please check the link in email again.");
				}
			},
			error: (error) => this.handleVerificationError(error)
		});
	}

	/**
	 * Handle Email Verification Error
	 */
	private handleVerificationError(error: any): void {
		this._utilsService.alert('error', this._translate.instant('AUTH.FAIL_VERIFY_EMAIL'), error);
		this._utilsService.clearCookie();
		this._router.navigate(['/login']);
	}

	/**
	 * Handle Reset Password Success
	 */
	private handleResetPasswordSuccess(): void {
		this._utilsService.alert("success", this._translate.instant('ALERTS.SUCCESS'), this._translate.instant('ALERTS.PASSWORD_HAS_UPDATED_SUCCESSFULLY'));
		this._utilsService.clearCookie();
		this._router.navigate(['/login']);
	}

	/**
	 * Handle Reset Password Error
	 */
	private handleResetPasswordError(error: any): void {
		this.loading = false;
		this._utilsService.alert("error", this._translate.instant('ALERTS.FAIL_TO_UPDATE_PASSWORD'), this._translate.instant('AUTH.CHECK_LINK_EMAIL'));
		this._utilsService.clearCookie();
		this._router.navigate(['/login']);
	}
}
